import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PayrollBaseline, PayrollCycle } from "@kkp/api-client";
import { createFdsBaseline, getFdsBaseline } from "../../api/fdsSettings";
import { ApiError } from "../../api/client";
import { Button, Card, CardBody, CardHeader, CardTitle, toast } from "@kkp/ui";
import {
  Field as DisplayField,
  formatDateTime,
  formatIdr,
  FdsInactiveBanner,
  useCapabilities,
} from "@kkp/features";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/Dialog";
import { Field } from "../../ui/Input";
import { useFdsRecipients } from "./FdsRecipients";

const FDS_BASELINE_KEY = (companyId: string) => ["fds-baseline", companyId];

const CYCLE_ANCHOR_RANGE: Record<PayrollCycle, [number, number]> = {
  Weekly: [1, 7],
  Monthly: [1, 31],
};
const CYCLE_MAX_WINDOW: Record<PayrollCycle, number> = {
  Weekly: 3,
  Monthly: 15,
};
const WEEKDAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

function anchorDayLabel(cycle: PayrollCycle, anchorDay: number): string {
  if (cycle === "Weekly") {
    return WEEKDAY_NAMES[anchorDay - 1] ?? String(anchorDay);
  }
  return `Day ${anchorDay} of month`;
}

const inputClassName =
  "w-full rounded-xl border border-[color:rgba(255,255,255,0.12)] bg-[color:rgba(255,255,255,0.06)] px-3 py-2.5 text-[color:rgba(255,255,255,0.92)] placeholder:text-[color:rgba(255,255,255,0.35)] outline-none transition focus:border-[color:color-mix(in_oklab,var(--kkp-magenta)_70%,white)] focus:shadow-[0_0_0_3px_rgba(203,72,226,0.18)]";

export function FdsBaselineSection({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const { data: capabilities } = useCapabilities();
  const fdsEnabled = capabilities?.fds === true;
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const {
    data: history,
    isLoading,
    isError,
  } = useQuery({
    queryKey: FDS_BASELINE_KEY(companyId),
    queryFn: () => getFdsBaseline(companyId),
    enabled: Boolean(companyId),
    refetchOnMount: "always",
  });

  const onSaved = () => {
    setDialogOpen(false);
    void queryClient.invalidateQueries({
      queryKey: FDS_BASELINE_KEY(companyId),
    });
    void queryClient.invalidateQueries({
      queryKey: ["fds-recipients", companyId],
    });
  };

  return (
    <Card>
      <CardHeader className="flex items-center justify-between gap-3">
        <CardTitle as="h2">Approved payroll baseline</CardTitle>
        <Button
          variant="primary"
          size="sm"
          disabled={!fdsEnabled}
          onClick={() => setDialogOpen(true)}
        >
          New baseline version
        </Button>
      </CardHeader>

      <CardBody className="grid gap-4">
        <FdsInactiveBanner fds={capabilities?.fds} />
        <div className="text-xs text-[color:var(--kkp-text-muted)]">
          The approved baseline never changes from transactions — only a new
          admin-approved version replaces it.
        </div>

        {isError ? (
          <div className="text-sm text-[color:var(--kkp-text-muted)]">
            Failed to load the payroll baseline.
          </div>
        ) : null}
        {isLoading ? (
          <div className="text-sm text-[color:var(--kkp-text-muted)]">
            Loading…
          </div>
        ) : null}

        {history && !history.current ? (
          <div className="text-sm text-[color:var(--kkp-text-muted)]">
            No approved baseline yet.
          </div>
        ) : null}

        {history?.current ? (
          <CurrentBaseline baseline={history.current} />
        ) : null}

        {history && history.previous.length > 0 ? (
          <details className="text-sm">
            <summary className="cursor-pointer font-semibold text-[color:var(--kkp-text)]">
              History ({history.previous.length})
            </summary>
            <div className="mt-2 grid gap-2">
              {history.previous.map((b) => (
                <PreviousBaselineRow key={b.id} baseline={b} />
              ))}
            </div>
          </details>
        ) : null}
      </CardBody>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) setDialogOpen(false);
        }}
      >
        <DialogContent wide>
          {dialogOpen ? (
            <BaselineDialog
              companyId={companyId}
              current={history?.current ?? null}
              fdsEnabled={fdsEnabled}
              onClose={() => setDialogOpen(false)}
              onSaved={onSaved}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function CurrentBaseline({ baseline }: { baseline: PayrollBaseline }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
      <DisplayField label="Cycle" value={baseline.cycle} />
      <DisplayField
        label="Anchor"
        value={anchorDayLabel(baseline.cycle, baseline.anchorDay)}
      />
      <DisplayField label="Window (days)" value={String(baseline.windowDays)} />
      <DisplayField
        label="Approved total principal"
        value={formatIdr(baseline.approvedTotalPrincipal)}
      />
      <DisplayField
        label="Approved roster count"
        value={String(baseline.roster.length)}
      />
      <DisplayField label="Version" value={`v${baseline.version}`} />
      <DisplayField label="Approved by" value={baseline.approvedBy} />
      <DisplayField
        label="Approved at"
        value={formatDateTime(baseline.approvedAt)}
      />
      <DisplayField label="Evidence ref" value={baseline.evidenceRef} />
      <DisplayField label="Reason" value={baseline.reason} />
    </div>
  );
}

function PreviousBaselineRow({ baseline }: { baseline: PayrollBaseline }) {
  return (
    <div className="rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] px-3 py-2 text-sm">
      <div className="font-semibold">
        v{baseline.version} · {baseline.cycle} ·{" "}
        {formatIdr(baseline.approvedTotalPrincipal)}
      </div>
      <div className="text-[color:var(--kkp-text-muted)]">
        Approved by {baseline.approvedBy} on{" "}
        {formatDateTime(baseline.approvedAt)}
      </div>
      <div className="text-[color:var(--kkp-text-muted)]">
        {baseline.reason} ({baseline.evidenceRef})
      </div>
    </div>
  );
}

type RosterRowState = { included: boolean; principal: string };

function BaselineDialog({
  companyId,
  current,
  fdsEnabled,
  onClose,
  onSaved,
}: {
  companyId: string;
  current: PayrollBaseline | null;
  fdsEnabled: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { data: recipients } = useFdsRecipients(companyId);
  const activeRecipients = React.useMemo(
    () => (recipients ?? []).filter((r) => r.status === "Active"),
    [recipients],
  );

  const [cycle, setCycle] = React.useState<PayrollCycle>(
    current?.cycle ?? "Monthly",
  );
  const [anchorDay, setAnchorDay] = React.useState(current?.anchorDay ?? 1);
  const [windowDays, setWindowDays] = React.useState(current?.windowDays ?? 0);
  const [approvedTotalPrincipal, setApprovedTotalPrincipal] = React.useState(
    current?.approvedTotalPrincipal ?? "0",
  );
  const [reason, setReason] = React.useState("");
  const [evidenceRef, setEvidenceRef] = React.useState("");
  const [roster, setRoster] = React.useState<Record<string, RosterRowState>>(
    {},
  );
  const [formError, setFormError] = React.useState<string | null>(null);

  const prefilled = React.useRef(false);
  React.useEffect(() => {
    if (prefilled.current || !recipients) return;
    prefilled.current = true;
    const next: Record<string, RosterRowState> = {};
    for (const r of recipients) {
      if (r.status !== "Active") continue;
      next[r.seqId] = {
        included: r.approvedPrincipal !== null,
        principal: r.approvedPrincipal ?? "0",
      };
    }
    setRoster(next);
  }, [recipients]);

  const handleCycleChange = (next: PayrollCycle) => {
    setCycle(next);
    const [minAnchor, maxAnchor] = CYCLE_ANCHOR_RANGE[next];
    setAnchorDay((d) => Math.min(Math.max(d, minAnchor), maxAnchor));
    const maxWindow = CYCLE_MAX_WINDOW[next];
    setWindowDays((d) => Math.min(Math.max(d, 0), maxWindow));
  };

  const rosterSum = React.useMemo(
    () =>
      Object.values(roster)
        .filter((r) => r.included)
        .reduce((acc, r) => acc + (Number(r.principal) || 0), 0),
    [roster],
  );

  const validationError = React.useMemo(() => {
    const [minAnchor, maxAnchor] = CYCLE_ANCHOR_RANGE[cycle];
    if (
      !Number.isInteger(anchorDay) ||
      anchorDay < minAnchor ||
      anchorDay > maxAnchor
    ) {
      return `Anchor day must be between ${minAnchor} and ${maxAnchor} for a ${cycle} cycle.`;
    }
    const maxWindow = CYCLE_MAX_WINDOW[cycle];
    if (
      !Number.isInteger(windowDays) ||
      windowDays < 0 ||
      windowDays > maxWindow
    ) {
      return `Window days must be between 0 and ${maxWindow} for a ${cycle} cycle.`;
    }
    const total = Number(approvedTotalPrincipal);
    if (!Number.isFinite(total) || total < 0) {
      return "Approved total principal must be a non-negative amount.";
    }
    if (!reason.trim()) return "Reason is required.";
    if (!evidenceRef.trim()) return "Evidence ref is required.";
    return null;
  }, [
    cycle,
    anchorDay,
    windowDays,
    approvedTotalPrincipal,
    reason,
    evidenceRef,
  ]);

  const mutation = useMutation({
    mutationFn: () =>
      createFdsBaseline(companyId, {
        cycle,
        anchorDay,
        windowDays,
        approvedTotalPrincipal: approvedTotalPrincipal.trim(),
        reason: reason.trim(),
        evidenceRef: evidenceRef.trim(),
        roster: Object.entries(roster)
          .filter(([, v]) => v.included)
          .map(([seqId, v]) => ({
            recipientSeqId: seqId,
            approvedPrincipal: v.principal.trim(),
          })),
      }),
    onSuccess: () => {
      toast.success("New baseline version approved.", { title: "Saved" });
      onSaved();
    },
    onError: (err: unknown) => {
      const message =
        err instanceof ApiError ? err.message : "Failed to save baseline.";
      setFormError(message);
      toast.danger(message, { title: "Error" });
    },
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>New baseline version</DialogTitle>
        <DialogDescription>
          The approved baseline never changes from transactions — saving here
          creates a new, append-only version.
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Cycle" required>
          <select
            aria-label="Cycle"
            className={inputClassName}
            value={cycle}
            onChange={(e) => handleCycleChange(e.target.value as PayrollCycle)}
          >
            <option value="Weekly">Weekly</option>
            <option value="Monthly">Monthly</option>
          </select>
        </Field>
        <Field
          label="Anchor day"
          hint={
            cycle === "Weekly"
              ? "ISO weekday, Monday = 1"
              : "Day of month, 1–31"
          }
          required
        >
          <input
            aria-label="Anchor day"
            type="number"
            className={inputClassName}
            value={anchorDay}
            onChange={(e) => setAnchorDay(Number(e.target.value))}
          />
        </Field>
        <Field label="Window days" required>
          <input
            aria-label="Window days"
            type="number"
            className={inputClassName}
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
          />
        </Field>
        <Field label="Approved total principal (IDR)" required>
          <input
            aria-label="Approved total principal"
            className={inputClassName}
            value={approvedTotalPrincipal}
            onChange={(e) => setApprovedTotalPrincipal(e.target.value)}
          />
        </Field>
      </div>

      <div className="mt-3 grid gap-2">
        <div className="flex items-center justify-between text-xs text-[color:var(--kkp-text-muted)]">
          <span>Roster (active recipients)</span>
          <span>
            Sum of included principals:{" "}
            <span className="font-semibold text-[color:var(--kkp-text)]">
              {formatIdr(String(rosterSum))}
            </span>{" "}
            — informational only, need not match the approved total.
          </span>
        </div>
        <div className="max-h-64 overflow-y-auto rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)]">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-[color:var(--kkp-border)] text-left text-xs text-[color:var(--kkp-text-muted)]">
                <th className="py-2 pl-3 pr-2">Include</th>
                <th className="py-2 pr-2">Name</th>
                <th className="py-2 pr-3">Approved principal (IDR)</th>
              </tr>
            </thead>
            <tbody>
              {activeRecipients.map((r) => {
                const row = roster[r.seqId] ?? {
                  included: false,
                  principal: "0",
                };
                return (
                  <tr
                    key={r.seqId}
                    className="border-b border-[color:var(--kkp-border)] text-sm"
                  >
                    <td className="py-1.5 pl-3 pr-2">
                      <input
                        type="checkbox"
                        aria-label={`Include ${r.fullName}`}
                        checked={row.included}
                        onChange={() =>
                          setRoster((prev) => ({
                            ...prev,
                            [r.seqId]: { ...row, included: !row.included },
                          }))
                        }
                      />
                    </td>
                    <td className="py-1.5 pr-2">{r.fullName}</td>
                    <td className="py-1.5 pr-3">
                      <input
                        aria-label={`Approved principal for ${r.fullName}`}
                        className={inputClassName}
                        disabled={!row.included}
                        value={row.principal}
                        onChange={(e) =>
                          setRoster((prev) => ({
                            ...prev,
                            [r.seqId]: { ...row, principal: e.target.value },
                          }))
                        }
                      />
                    </td>
                  </tr>
                );
              })}
              {activeRecipients.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    className="py-3 text-center text-sm text-[color:var(--kkp-text-muted)]"
                  >
                    No active recipients.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-3 grid gap-3">
        <Field label="Reason" required>
          <textarea
            rows={2}
            className={inputClassName}
            placeholder="Required — why is this baseline being approved?"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        <Field label="Evidence ref" required>
          <input
            className={inputClassName}
            placeholder="e.g. BO-015"
            value={evidenceRef}
            onChange={(e) => setEvidenceRef(e.target.value)}
          />
        </Field>
      </div>

      {formError ? (
        <div className="mt-2 text-xs font-semibold text-[color:rgba(239,68,68,0.95)]">
          {formError}
        </div>
      ) : null}

      <DialogFooter>
        <Button
          variant="ghost"
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          type="button"
          disabled={
            !fdsEnabled || validationError !== null || mutation.isPending
          }
          title={validationError ?? undefined}
          onClick={() => {
            setFormError(null);
            mutation.mutate();
          }}
        >
          {mutation.isPending ? "Saving…" : "Save new version"}
        </Button>
      </DialogFooter>
    </>
  );
}
